import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import Spotlight from "@/components/Spotlight";
import HeroVisual from "@/components/HeroVisual";
import EditorialMarquee from "@/components/EditorialMarquee";
import DecisionLoop from "@/components/DecisionLoop";
import InsightCard from "@/components/InsightCard";
import BookCover from "@/components/BookCover";
import { articles, homepageEvidence, publicLinks, thinkingAreas } from "@/data/siteData";

export default function Home() {
  return (
    <main data-testid="home-page">
      <Spotlight className="hero-section">
        <div className="shell hero-editorial-shell">
          <div className="hero-editorial-top">
            <div className="hero-copy">
              <div className="eyebrow">Ashwini Patil / Data, AI &amp; Decision Intelligence</div>
              <h1 className="hero-title" data-testid="hero-title-main">
                <span className="hero-line">I work on the distance</span>
                <span className="hero-line">between what data can show</span>
                <span className="hero-line"><em>and what decisions require.</em></span>
              </h1>
              <p className="hero-standfirst">I work with leaders who are trying to turn years of data and analytics investment into decisions people can trust — and learn from.</p>
              <div className="hero-actions">
                <Link to="/insights" className="button-primary" data-testid="hero-insights-link">Read the thinking <ArrowUpRight size={15} /></Link>
                <Link to="/frameworks" className="button-quiet" data-testid="hero-frameworks-link">Explore Decision Intelligence <ArrowUpRight size={15} /></Link>
              </div>
              <div className="hero-caption"><span className="signal-dot" /> Data &amp; analytics modernization · Enterprise AI · Human judgment</div>
            </div>
            <aside className="hero-editorial-note" data-testid="hero-editorial-note">
              <span className="section-kicker">01 / A working thesis</span>
              <p>Architecture earns trust when people can trace how a signal became a decision.</p>
            </aside>
          </div>
          <HeroVisual />
        </div>
      </Spotlight>

      <EditorialMarquee />

      <section className="page-section" id="featured-thinking" data-testid="featured-thinking-section">
        <div className="shell">
          <Reveal>
            <div className="section-heading">
              <div><div className="section-kicker">01 / Where the work meets the questions</div><h2 className="section-title" data-testid="featured-thinking-title">Better decisions begin long before the moment of choice.</h2></div>
              <p className="section-summary">Over the years, I have seen that the harder work is making the foundation, the meaning, the accountability and the learning fit together. More data or another model rarely answers that question by itself.</p>
            </div>
          </Reveal>
          <div className="thinking-grid">
            {thinkingAreas.map((area, index) => (
              <Reveal key={area.index} delay={index * 90}>
                <article className="thinking-card" data-testid={`thinking-card-${area.index}`}>
                  <span className="card-index">{area.index} / where I start</span>
                  <h3 className="card-title">{area.title}</h3>
                  <p className="card-copy">{area.copy}</p>
                  <Link to={area.path} className="card-link" data-testid={`thinking-link-${area.index}`}>Follow the thread <ArrowUpRight size={15} /></Link>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="page-section decision-section" data-testid="decision-intelligence-section">
        <div className="shell">
          <Reveal>
            <div className="decision-intro">
              <div><div className="section-kicker">02 / The idea I keep returning to</div><h2 className="section-title" data-testid="decision-system-title">The Decision Intelligence System</h2></div>
              <p className="section-summary">I think of Decision Intelligence as the connective tissue between a trusted data foundation and the people who have to make a consequential choice. It gives the outcome somewhere to go next.</p>
            </div>
          </Reveal>
          <Reveal delay={140}><DecisionLoop /></Reveal>
        </div>
      </section>

      <section className="page-section" data-testid="home-insights-section">
        <div className="shell">
          <Reveal>
            <div className="section-heading">
              <div><div className="section-kicker">03 / Notes from inside the work</div><h2 className="section-title">Questions I have carried from one transformation to the next.</h2></div>
              <div className="section-heading-actions"><Link to="/insights" className="inline-link" data-testid="home-all-insights-link">Read all insights <ArrowUpRight size={15} /></Link><Link to="/articles" className="inline-link" data-testid="home-articles-discovery-link">External articles <ArrowUpRight size={15} /></Link></div>
            </div>
          </Reveal>
          <div className="insight-grid">
            {articles.slice(0, 3).map((article, index) => <Reveal key={article.slug} delay={index * 90}><InsightCard article={article} /></Reveal>)}
          </div>
        </div>
      </section>

      <section className="page-section experience-band" data-testid="experience-section">
        <div className="shell">
          <div className="experience-grid">
            <Reveal><div className="section-kicker">04 / Experience behind the thinking</div><h2 className="experience-title">The questions came from the work.</h2></Reveal>
            <Reveal delay={120}><p className="experience-copy">I have spent my career working across enterprise data, analytics modernization, architecture and technology transformation. The deeper question emerged slowly: how can trusted data, clear context and governed AI help people make important decisions with more confidence?</p></Reveal>
          </div>
          <div className="credibility-grid" data-testid="experience-credentials">
            <Reveal><div className="credibility-signal"><span className="metric-number">SENIOR</span><span className="metric-label">Leadership track record across enterprise data, analytics and architecture</span></div></Reveal>
            <Reveal delay={80}><div className="credibility-signal"><span className="metric-number">MULTIPLE</span><span className="metric-label">Industries, operating models and transformation questions</span></div></Reveal>
            <Reveal delay={160}><div className="credibility-signal"><span className="metric-number">GLOBAL</span><span className="metric-label">Enterprise environments where change has to work beyond the pilot</span></div></Reveal>
          </div>
          <div className="selected-transformations" data-testid="selected-transformations">
            <div className="section-kicker">Patterns I have learned from / 05</div>
            <div className="selected-transformation-grid">
              {homepageEvidence.map((item, index) => <Reveal key={item.label} delay={index * 90}><article className="selected-transformation-card" data-testid={`selected-transformation-${index + 1}`}><span className="card-index">0{index + 1} / {item.label}</span><h3>{item.title}</h3><p>{item.copy}</p><span className="selected-transformation-tags">{item.tags}</span></article></Reveal>)}
            </div>
          </div>
        </div>
      </section>

      <section className="page-section" data-testid="book-teaser-section">
        <div className="shell book-teaser">
          <Reveal><BookCover /></Reveal>
          <Reveal className="book-copy" delay={120}>
            <div className="section-kicker">06 / The book</div>
            <h2 className="section-title">The Decision Gap</h2>
            <p>I wrote this book because better data and more capable AI still leave important decisions hard to make well. It is my attempt to name the space between intelligence and action, and to ask what an organization needs in order to remember, evaluate and learn from its choices.</p>
            <Link to="/book" className="inline-link" data-testid="home-book-link">Read the book overview <ArrowUpRight size={15} /></Link>
          </Reveal>
        </div>
      </section>

      <section className="page-section decision-section" data-testid="home-connect-section">
        <div className="shell">
          <Reveal>
            <div className="section-kicker">07 / Conversation</div>
            <h2 className="section-title">If this is a question you are working on, I would like to hear about it.</h2>
            <p className="home-connect-copy">I am interested in the practical questions around data modernization, platform architecture, semantic foundations, governed AI and the decisions that connect them.</p>
            <div className="hero-actions">
              <a href={publicLinks.linkedin} target="_blank" rel="noreferrer noopener" className="button-primary" data-testid="home-linkedin-link">Find me on LinkedIn <ArrowUpRight size={15} /></a>
              <Link to="/speaking" className="button-quiet" data-testid="home-speaking-link">Speaking &amp; conversations <ArrowUpRight size={15} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
